/* AdminAnalytics — iter98.99du
 *
 * Tiny analytics dashboard surfaced at /admin/analytics. Today it renders
 * a single funnel (Book Scan Visit click → confirmation) so the admin can
 * eyeball whether the new rose CTA in PatientProfile is actually driving
 * more bookings. Designed to be the home for additional funnels (case-
 * submit, plan-approval, payment-confirm, etc.) as we instrument more
 * events.
 *
 * Data source: GET /api/admin/analytics/scan-visit-funnel?days=N
 * (no client-side caching beyond the page lifecycle — refetches on
 * window-selector change so the admin always sees fresh numbers).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    ChartLineUp,
    CursorClick,
    CheckCircle,
    Percent,
    Users,
    ArrowsClockwise,
    Phone,
    ChatCircleDots,
    MapPin,
    PhoneIncoming,
    PhoneSlash,
    ClockClockwise,
    XCircle,
    Trophy,
    ArrowUpRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import CancellationReasonsWidget from "@/components/admin/CancellationReasonsWidget";
import { ProductionKpisWidget } from "@/components/admin/ProductionKpisWidget";

// iter98.99et — Outcome chip → tone + icon. Mirrors the source-of-truth
// OUTCOME_CHIPS in CoeLeadDetail.jsx so the admin view and the PES
// view always read the same.
const OUTCOME_VISUAL = {
    answered:   { Icon: PhoneIncoming,  bar: "from-emerald-200 to-emerald-300", text: "text-emerald-800", pill: "bg-emerald-100 text-emerald-800" },
    no_answer:  { Icon: PhoneSlash,     bar: "from-amber-200 to-amber-300",     text: "text-amber-800",   pill: "bg-amber-100 text-amber-800" },
    callback:   { Icon: ClockClockwise, bar: "from-violet-200 to-violet-300",   text: "text-violet-700",  pill: "bg-violet-100 text-violet-700" },
    wrong_num:  { Icon: XCircle,        bar: "from-rose-200 to-rose-300",       text: "text-rose-700",    pill: "bg-rose-100 text-rose-700" },
};

const WINDOWS = [
    { days: 1,  label: "Today" },
    { days: 7,  label: "7 days" },
    { days: 30, label: "30 days" },
    { days: 90, label: "90 days" },
];

function fmtNum(n) {
    if (n == null) return "—";
    return Number(n).toLocaleString("en-IN");
}

export default function AdminAnalytics() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [days, setDays] = useState(30);
    // iter100.239 — Tab state initializes from `?tab=X` URL param, and
    // writes back on every switch so deep-linked and bookmarked tabs work.
    // Values outside the whitelist fall back to "acquisition".
    // iter101.26 — Added `aligner_cases` so the Production KPIs +
    // Cancellation Reasons widgets (previously living on the Aligner
    // Cases page footer) surface in the same tab shelf as every other
    // analytics view. Keeps operational analytics in one place.
    const VALID_TABS = ["acquisition", "product", "ops", "aligner_cases"];
    const initialTab = (() => {
        const t = searchParams.get("tab");
        return VALID_TABS.includes(t) ? t : "acquisition";
    })();
    const [activeTab, setActiveTabState] = useState(initialTab);
    const setActiveTab = (t) => {
        setActiveTabState(t);
        const next = new URLSearchParams(searchParams);
        if (t === "acquisition") next.delete("tab");
        else next.set("tab", t);
        setSearchParams(next, { replace: true });
    };
    const [data, setData] = useState(null);
    const [enrich, setEnrich] = useState(null);          // iter98.99eq — Callback enrichment funnel
    const [enrichLoading, setEnrichLoading] = useState(true);
    const [outcomeMix, setOutcomeMix] = useState(null);  // iter98.99et — Contact-outcome heatmap
    const [outcomeLoading, setOutcomeLoading] = useState(true);
    const [coeOptin, setCoeOptin] = useState(null);      // iter100.235 — CoE-Support opt-in funnel
    const [coeOptinLoading, setCoeOptinLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/analytics/scan-visit-funnel?days=${days}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                toast.error(e.response?.data?.detail || "Couldn't load funnel");
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [days, refreshKey]);

    useEffect(() => {
        setEnrichLoading(true);
        api.get(`/admin/analytics/callback-enrichment-funnel?days=${days}`)
            .then((r) => setEnrich(r.data))
            .catch(() => setEnrich(null))
            .finally(() => setEnrichLoading(false));
    }, [days, refreshKey]);

    useEffect(() => {
        setOutcomeLoading(true);
        api.get(`/admin/analytics/contact-outcome-mix?days=${days}`)
            .then((r) => setOutcomeMix(r.data))
            .catch(() => setOutcomeMix(null))
            .finally(() => setOutcomeLoading(false));
    }, [days, refreshKey]);

    useEffect(() => {
        setCoeOptinLoading(true);
        api.get(`/admin/analytics/coe-support-optin?days=${days}`)
            .then((r) => setCoeOptin(r.data))
            .catch(() => setCoeOptin(null))
            .finally(() => setCoeOptinLoading(false));
    }, [days, refreshKey]);

    const clicks = data?.clicks ?? 0;
    const confirms = data?.confirmations ?? 0;
    const rate = data?.conversion_rate_pct ?? 0;
    const distClick = data?.distinct_doctors_clicked ?? 0;
    const distConfirm = data?.distinct_doctors_confirmed ?? 0;
    // Funnel bar widths — visualise the click→confirmation drop as two
    // stacked horizontal bars. The "clicks" bar is always 100% of its
    // track; the "confirmations" bar scales relative to clicks so the
    // dropoff is immediately visible. If clicks==0, both bars show 0.
    const confirmBarPct = clicks > 0 ? Math.min(100, Math.round((confirms / clicks) * 100)) : 0;

    return (
        <div className="p-6 space-y-6 max-w-5xl" data-testid="admin-analytics-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
                            <ChartLineUp size={18} weight="duotone" />
                        </div>
                        <h1 className="text-2xl font-display font-bold tracking-tight">Product analytics</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5 ml-11">
                        Conversion funnels for high-intent flows. New event sources land here as we instrument them.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Window selector pill group */}
                    <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="analytics-window-group">
                        {WINDOWS.map((w) => (
                            <button
                                key={w.days}
                                onClick={() => setDays(w.days)}
                                className={`text-xs font-semibold px-3 h-7 rounded-full transition-colors ${
                                    days === w.days
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                                }`}
                                data-testid={`analytics-window-${w.days}`}
                            >
                                {w.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setRefreshKey((k) => k + 1)}
                        className="w-9 h-9 grid place-items-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        title="Refresh"
                        data-testid="analytics-refresh-btn"
                    >
                        <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
                    </button>
                </div>
            </div>

            {/* iter100.236 — Executive KPI Row. Above-the-fold snapshot of
                every funnel. Click any tile → jumps to the matching
                sub-dashboard tab. Delta pills = period-over-period
                comparison. Sparklines = daily trend inside the window. */}
            <section data-testid="analytics-exec-row">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <KpiTile
                        label="Scan visits"
                        value={data?.confirmations ?? 0}
                        previous={data?.previous_period?.confirmations}
                        sparkline={data?.sparkline}
                        sparklineColor="text-rose-500"
                        loading={loading}
                        onClick={() => {
                            setActiveTab("acquisition");
                            requestAnimationFrame(() => document.querySelector("[data-testid='scan-visit-funnel-card']")?.scrollIntoView({ behavior: "smooth", block: "start" }));
                        }}
                        testid="kpi-scan-visits"
                    />
                    <KpiTile
                        label="Callbacks"
                        value={enrich?.total_callbacks ?? 0}
                        previous={enrich?.previous_period?.total_callbacks}
                        sparkline={enrich?.sparkline}
                        sparklineColor="text-amber-500"
                        loading={enrichLoading}
                        onClick={() => {
                            setActiveTab("acquisition");
                            requestAnimationFrame(() => document.querySelector("[data-testid='callback-enrichment-funnel']")?.scrollIntoView({ behavior: "smooth", block: "start" }));
                        }}
                        testid="kpi-callbacks"
                    />
                    <KpiTile
                        label="Reach rate"
                        value={outcomeMix?.reach_rate_pct ?? 0}
                        previous={outcomeMix?.previous_period?.reach_rate_pct}
                        sparkline={outcomeMix?.sparkline}
                        sparklineColor="text-emerald-500"
                        loading={outcomeLoading}
                        valueSuffix="%"
                        onClick={() => {
                            setActiveTab("ops");
                            requestAnimationFrame(() => document.querySelector("[data-testid='contact-outcome-heatmap']")?.scrollIntoView({ behavior: "smooth", block: "start" }));
                        }}
                        testid="kpi-reach-rate"
                    />
                    <KpiTile
                        label="Cases submitted"
                        value={coeOptin?.cases_submitted ?? 0}
                        previous={coeOptin?.previous_period?.cases_submitted}
                        sparkline={coeOptin?.sparkline}
                        sparklineColor="text-teal-500"
                        loading={coeOptinLoading}
                        onClick={() => {
                            setActiveTab("product");
                            requestAnimationFrame(() => document.querySelector("[data-testid='coe-optin-funnel-card']")?.scrollIntoView({ behavior: "smooth", block: "start" }));
                        }}
                        testid="kpi-cases-submitted"
                    />
                    <KpiTile
                        label="CoE opt-in"
                        value={coeOptin?.opt_in_pct ?? 0}
                        previous={coeOptin?.previous_period?.opt_in_pct}
                        sparkline={coeOptin?.sparkline}
                        sparklineColor="text-teal-500"
                        loading={coeOptinLoading}
                        valueSuffix="%"
                        onClick={() => {
                            setActiveTab("product");
                            requestAnimationFrame(() => document.querySelector("[data-testid='coe-optin-funnel-card']")?.scrollIntoView({ behavior: "smooth", block: "start" }));
                        }}
                        testid="kpi-coe-optin"
                    />
                </div>
            </section>

            {/* iter100.236 — Sub-dashboard tabs. Groups funnels by product
                surface so the page stays scannable as more funnels are
                added. Acquisition = top-of-funnel; Product = doctor
                behaviour; Ops = internal PES performance. */}
            <div className="border-b border-border" data-testid="analytics-tabs">
                <nav className="flex gap-6" aria-label="Analytics sections">
                    {[
                        { id: "acquisition",   label: "Acquisition",   note: "Leads · Callbacks · Scan visits" },
                        { id: "product",       label: "Product",       note: "Case submissions · CoE opt-in" },
                        { id: "ops",           label: "Ops",           note: "Counsellor outcomes · Reach rate" },
                        { id: "aligner_cases", label: "Aligner cases", note: "Production KPIs · Cancellations" },
                    ].map((t) => {
                        const isActive = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                data-testid={`analytics-tab-${t.id}`}
                                className={`relative pb-3 pt-1 text-left transition-colors ${
                                    isActive
                                        ? "text-primary"
                                        : "text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                <div className={`text-[13px] font-bold ${isActive ? "" : ""}`}>{t.label}</div>
                                <div className="text-[10.5px] text-muted-foreground/80 mt-0.5">{t.note}</div>
                                {isActive && (
                                    <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary rounded-t" />
                                )}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {activeTab === "acquisition" && (<>
            {/* Funnel #1 — Book Scan Visit */}
            <section
                className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
                data-testid="scan-visit-funnel-card"
            >
                <header className="px-5 pt-5 pb-3 border-b border-border bg-gradient-to-r from-rose-50/60 via-card to-card">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-rose-600">
                                IO Scans by Invisique
                            </div>
                            <p className="text-[12.5px] text-muted-foreground mt-1.5 leading-snug max-w-2xl">
                                Doctors book technician visits for intraoral scans. This metric tracks how many discover and adopt Invisique&apos;s on-site scanning service.
                            </p>
                        </div>
                        {data?.since && (
                            <div className="text-[10.5px] text-muted-foreground" data-testid="funnel-since">
                                Since {new Date(data.since).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                            </div>
                        )}
                    </div>
                </header>

                {/* KPI strip */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border">
                    <Metric
                        icon={CursorClick}
                        label="Clicks"
                        value={fmtNum(clicks)}
                        sub={`${distClick} distinct doctor${distClick === 1 ? "" : "s"}`}
                        testid="metric-clicks"
                        href="/admin/scan-visits?from=analytics"
                    />
                    <Metric
                        icon={CheckCircle}
                        label="Confirmations"
                        value={fmtNum(confirms)}
                        sub={`${distConfirm} distinct doctor${distConfirm === 1 ? "" : "s"}`}
                        testid="metric-confirmations"
                        href="/admin/scan-visits?from=analytics"
                    />
                    <Metric
                        icon={Percent}
                        label="Conversion"
                        value={`${rate}%`}
                        sub={clicks > 0 ? `${confirms} of ${clicks} clicks` : "No data yet"}
                        accent
                        testid="metric-conversion"
                        delta={<DeltaPill current={rate} previous={data?.previous_period?.conversion_rate_pct} />}
                    />
                </div>

                {/* Visual funnel bar */}
                <div className="px-5 py-5 space-y-4" data-testid="funnel-bars">
                    <FunnelBar
                        label="Clicks"
                        value={clicks}
                        widthPct={clicks > 0 ? 100 : 0}
                        toneCls="bg-rose-200/70"
                        valueCls="text-rose-700"
                        testid="bar-clicks"
                    />
                    <FunnelBar
                        label="Confirmations"
                        value={confirms}
                        widthPct={confirmBarPct}
                        toneCls="bg-emerald-300/80"
                        valueCls="text-emerald-700"
                        rightHint={clicks > 0 ? `${confirmBarPct}% of clicks` : ""}
                        testid="bar-confirmations"
                    />
                </div>

                {!loading && clicks === 0 && (
                    <div className="px-5 pb-5 text-[12px] text-muted-foreground italic" data-testid="funnel-empty">
                        No clicks recorded in this window yet. Once doctors start interacting with the rose CTA, numbers will populate here.
                    </div>
                )}
            </section>

            {/* iter98.99eq — Callback enrichment funnel. How many people
                who tapped the landing card also filled the optional
                "Tell us more" step. The Top concerns + Top cities
                lists tell us what callers actually want to talk about. */}
            <section
                className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
                data-testid="callback-enrichment-funnel"
            >
                <header className="px-5 pt-5 pb-3 border-b border-border bg-gradient-to-r from-amber-50/60 via-card to-card">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber-700">
                                Specialist Counsellor Callback
                            </div>
                            <p className="text-[12.5px] text-muted-foreground mt-1.5 leading-snug max-w-2xl">
                                Prospects request a callback from our specialist counsellor. This metric tracks how many share extra context (city + concern) so the callback lands warmer.
                            </p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                            {enrich?.window_days ?? days}-day window
                        </span>
                    </div>
                </header>

                <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
                    <Metric
                        icon={Phone}
                        label="Total callbacks"
                        value={enrichLoading ? "—" : fmtNum(enrich?.total_callbacks ?? 0)}
                        sub="Source = counsellor-callback"
                        testid="enrich-metric-total"
                        href="/admin/leads?tab=web-leads&from=analytics"
                    />
                    <Metric
                        icon={CheckCircle}
                        label="Enriched"
                        value={enrichLoading ? "—" : fmtNum(enrich?.enriched ?? 0)}
                        sub="City and/or concern given"
                        testid="enrich-metric-enriched"
                        href="/admin/leads?tab=web-leads&from=analytics"
                    />
                    <Metric
                        icon={Percent}
                        label="Completion rate"
                        value={enrichLoading ? "—" : `${enrich?.completion_rate_pct ?? 0}%`}
                        sub="Higher = warmer pipeline"
                        accent
                        testid="enrich-metric-rate"
                        delta={<DeltaPill current={enrich?.completion_rate_pct} previous={enrich?.previous_period?.completion_rate_pct} />}
                    />
                </div>

                {/* Top concerns + cities, side-by-side on desktop */}
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                    <EnrichBreakdownList
                        title="Top concerns"
                        icon={ChatCircleDots}
                        rows={(enrich?.top_concerns || []).map((r) => ({ label: r.label, count: r.count }))}
                        loading={enrichLoading}
                        emptyText="No concerns recorded yet. Once callers tap a concern chip, the breakdown lands here."
                        testid="enrich-top-concerns"
                    />
                    <EnrichBreakdownList
                        title="Top cities"
                        icon={MapPin}
                        rows={(enrich?.top_cities || []).map((r) => ({ label: r.city, count: r.count }))}
                        loading={enrichLoading}
                        emptyText="No cities recorded yet. Cities appear as callers fill the enrichment step."
                        testid="enrich-top-cities"
                    />
                </div>
            </section>

            </>)}

            {activeTab === "ops" && (<>
            {/* iter98.99et — Contact-outcome heatmap. Aggregates the
                outcome chip stamped on each PES note. Overall mix at the
                top, per-PES leaderboard below. Reach rate = (answered +
                callback) / (total - wrong_num) so a PES who weeds out
                bad numbers doesn't get penalised. */}
            <section
                className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
                data-testid="contact-outcome-heatmap"
            >
                <header className="px-5 pt-5 pb-3 border-b border-border bg-gradient-to-r from-emerald-50/60 via-card to-card">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                                Counsellor Team Performance
                            </div>
                            <p className="text-[12.5px] text-muted-foreground mt-1.5 leading-snug max-w-2xl">
                                Counsellors log an outcome on every callback attempt. This metric tracks the team&apos;s reach rate and highlights top performers.
                            </p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                            {outcomeMix?.window_days ?? days}-day window
                        </span>
                    </div>
                </header>

                {/* 4-up outcome mix tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border">
                    {(outcomeMix?.by_outcome || [
                        { id: "answered", label: "Answered" },
                        { id: "no_answer", label: "No answer" },
                        { id: "callback", label: "Callback later" },
                        { id: "wrong_num", label: "Wrong number" },
                    ]).map((o) => {
                        const v = OUTCOME_VISUAL[o.id] || OUTCOME_VISUAL.answered;
                        return (
                            <div className="p-5" key={o.id} data-testid={`outcome-tile-${o.id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <v.Icon size={14} weight="fill" className={v.text} />
                                    <span className={`text-[10.5px] font-bold uppercase tracking-[0.14em] ${v.text}`}>{o.label}</span>
                                </div>
                                <div className="text-2xl font-display font-bold tabular-nums leading-none">
                                    {outcomeLoading ? "—" : `${o.pct ?? 0}%`}
                                </div>
                                <div className="text-[11.5px] text-muted-foreground mt-1 tabular-nums">
                                    {outcomeLoading ? "" : `${fmtNum(o.count ?? 0)} logged`}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Per-PES leaderboard */}
                <div className="border-t border-border p-5" data-testid="outcome-pes-leaderboard">
                    <div className="flex items-center gap-2 mb-3">
                        <Trophy size={14} weight="duotone" className="text-emerald-700" />
                        <h3 className="text-[12.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Per-counsellor reach rate</h3>
                        <span className="text-[10px] text-muted-foreground italic ml-1">(answered + callback) ÷ reachable</span>
                    </div>
                    {outcomeLoading ? (
                        <div className="text-[12px] text-muted-foreground italic">Loading…</div>
                    ) : (outcomeMix?.by_pes || []).length === 0 ? (
                        <div className="text-[12px] text-muted-foreground italic leading-relaxed">
                            No outcomes have been logged yet. As counsellors log outcomes after calls, the leaderboard fills in here.
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-5 px-5">
                            <table className="w-full text-[12.5px]">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                        <th className="text-left font-bold py-2 pr-3">Counsellor</th>
                                        <th className="text-right font-bold py-2 px-2 w-20">Reach</th>
                                        <th className="text-right font-bold py-2 px-2 w-16">Ans</th>
                                        <th className="text-right font-bold py-2 px-2 w-16">No-ans</th>
                                        <th className="text-right font-bold py-2 px-2 w-16">Cb</th>
                                        <th className="text-right font-bold py-2 px-2 w-16">Bad #</th>
                                        <th className="text-right font-bold py-2 pl-2 w-16">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {outcomeMix.by_pes.map((p, idx) => (
                                        <tr
                                            key={p.author_id}
                                            className="border-t border-border"
                                            data-testid={`outcome-pes-row-${p.author_id}`}
                                        >
                                            <td className="py-2.5 pr-3 font-medium flex items-center gap-2">
                                                {idx === 0 && <Trophy size={12} weight="fill" className="text-amber-500" />}
                                                {p.author_name}
                                            </td>
                                            <td className="py-2.5 px-2 text-right tabular-nums">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
                                                    {p.reach_rate_pct}%
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-2 text-right tabular-nums">{p.answered}</td>
                                            <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground">{p.no_answer}</td>
                                            <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground">{p.callback}</td>
                                            <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground">{p.wrong_num}</td>
                                            <td className="py-2.5 pl-2 text-right tabular-nums font-bold">{p.total}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>

            </>)}

            {activeTab === "product" && (<>
            {/* iter100.235 — CoE-Support opt-in funnel. Per-case boolean
                set by doctors in the New Case wizard. Source of truth is
                the `cases` collection itself — no client-side event
                required, so this section works retroactively across
                every case ever submitted. */}
            <section
                className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
                data-testid="coe-optin-funnel-card"
            >
                <header className="px-5 pt-5 pb-3 border-b border-border bg-gradient-to-r from-teal-50/60 via-card to-card">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-teal-700">
                                Invisique Compliance Monitoring
                            </div>
                            <p className="text-[12.5px] text-muted-foreground mt-1.5 leading-snug max-w-2xl">
                                Doctors opt-in per case for Invisique compliance monitoring. This metric tracks how many activate the service on their new cases.
                            </p>
                        </div>
                        {coeOptin?.since && (
                            <div className="text-[10.5px] text-muted-foreground" data-testid="coe-optin-since">
                                Since {new Date(coeOptin.since).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                            </div>
                        )}
                    </div>
                </header>

                {/* KPI strip — 4 metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
                    <Metric
                        icon={ChartLineUp}
                        label="Cases submitted"
                        value={coeOptinLoading ? "—" : fmtNum(coeOptin?.cases_submitted ?? 0)}
                        sub={
                            (coeOptin?.untracked ?? 0) > 0
                                ? `${fmtNum(coeOptin.untracked)} legacy (no toggle)`
                                : "Denominator"
                        }
                        testid="coe-optin-metric-submitted"
                        href="/admin/cases?from=analytics"
                    />
                    <Metric
                        icon={CheckCircle}
                        label="Opted IN"
                        value={coeOptinLoading ? "—" : fmtNum(coeOptin?.opted_in ?? 0)}
                        sub={
                            (coeOptin?.adopting_doctors ?? 0) > 0
                                ? `${coeOptin.adopting_doctors} distinct doctor${coeOptin.adopting_doctors === 1 ? "" : "s"}`
                                : "No adopters yet"
                        }
                        testid="coe-optin-metric-in"
                        href="/admin/cases?from=analytics"
                    />
                    <Metric
                        icon={XCircle}
                        label="Opted OUT"
                        value={coeOptinLoading ? "—" : fmtNum(coeOptin?.opted_out ?? 0)}
                        sub={
                            (coeOptin?.opted_out ?? 0) > 0
                                ? "Explicitly declined"
                                : "Nobody declined"
                        }
                        testid="coe-optin-metric-out"
                        href="/admin/cases?from=analytics"
                    />
                    <Metric
                        icon={Percent}
                        label="Opt-in rate"
                        value={coeOptinLoading ? "—" : `${coeOptin?.opt_in_pct ?? 0}%`}
                        sub={
                            (coeOptin?.doctor_adoption_pct ?? 0) > 0
                                ? `${coeOptin.doctor_adoption_pct}% of active doctors`
                                : "By case · doctor-level in sub"
                        }
                        accent
                        testid="coe-optin-metric-rate"
                        delta={<DeltaPill current={coeOptin?.opt_in_pct} previous={coeOptin?.previous_period?.opt_in_pct} />}
                    />
                </div>

                {/* Bar breakdown */}
                <div className="px-5 py-5 space-y-4" data-testid="coe-optin-bars">
                    <FunnelBar
                        label="Opted in"
                        value={coeOptin?.opted_in ?? 0}
                        widthPct={(() => {
                            const tracked = (coeOptin?.opted_in ?? 0) + (coeOptin?.opted_out ?? 0);
                            return tracked > 0
                                ? Math.round(((coeOptin?.opted_in ?? 0) / tracked) * 100)
                                : 0;
                        })()}
                        toneCls="bg-teal-300/80"
                        valueCls="text-teal-700"
                        rightHint={coeOptin?.opt_in_pct != null ? `${coeOptin.opt_in_pct}%` : ""}
                        testid="coe-optin-bar-in"
                    />
                    <FunnelBar
                        label="Opted out"
                        value={coeOptin?.opted_out ?? 0}
                        widthPct={(() => {
                            const tracked = (coeOptin?.opted_in ?? 0) + (coeOptin?.opted_out ?? 0);
                            return tracked > 0
                                ? Math.round(((coeOptin?.opted_out ?? 0) / tracked) * 100)
                                : 0;
                        })()}
                        toneCls="bg-slate-300/80"
                        valueCls="text-slate-700"
                        rightHint={
                            (() => {
                                const tracked = (coeOptin?.opted_in ?? 0) + (coeOptin?.opted_out ?? 0);
                                return tracked > 0
                                    ? `${100 - (coeOptin?.opt_in_pct ?? 0)}%`
                                    : "";
                            })()
                        }
                        testid="coe-optin-bar-out"
                    />
                </div>

                {!coeOptinLoading && (coeOptin?.cases_submitted ?? 0) === 0 && (
                    <div className="px-5 pb-5 text-[12px] text-muted-foreground italic" data-testid="coe-optin-empty">
                        No cases submitted in this window yet. Once doctors start submitting cases with the compliance-monitoring toggle, opt-in numbers will populate here.
                    </div>
                )}
            </section>
            </>)}

            {activeTab === "aligner_cases" && (<>
            {/* iter101.26 — Aligner Cases analytics. Moved here from the
                /admin/cases footer so every operational analytics view
                lives in the same tab shelf. Two widgets:
                  · ProductionKpisWidget    — throughput + manufacturing health
                  · CancellationReasonsWidget — churn signal (why cases drop) */}
            <ProductionKpisWidget />
            <div className="mt-4">
                <CancellationReasonsWidget />
            </div>
            </>)}

        </div>
    );
}

function Metric({ icon: Icon, label, value, sub, accent, testid, delta, href }) {
    const inner = (
        <>
            <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                <Icon size={12} weight="duotone" />
                {label}
                {href && (
                    <ArrowUpRight size={10} weight="bold" className="text-muted-foreground/50 ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
                <div className={`font-display text-[28px] font-bold tabular-nums leading-none ${accent ? "text-rose-600" : "text-foreground"}`}>
                    {value}
                </div>
                {delta}
            </div>
            {sub && (
                <div className="text-[11px] text-muted-foreground mt-1.5 inline-flex items-center gap-1">
                    <Users size={11} weight="duotone" /> {sub}
                </div>
            )}
        </>
    );
    // iter100.240 — When `href` is provided the tile becomes a drill-in
    // link to the source-of-truth admin page (e.g. clicking "2
    // confirmations" navigates to /admin/scan-visits). Rate/percentage
    // metrics never get an href since they're derived, not enumerable.
    if (href) {
        return (
            <Link
                to={href}
                className="group px-5 py-4 bg-card hover:bg-secondary/40 transition-colors cursor-pointer block"
                data-testid={testid}
            >
                {inner}
            </Link>
        );
    }
    return (
        <div
            className={`px-5 py-4 bg-card ${accent ? "md:border-l md:border-rose-100" : ""}`}
            data-testid={testid}
        >
            {inner}
        </div>
    );
}

function FunnelBar({ label, value, widthPct, toneCls, valueCls, rightHint, testid }) {
    return (
        <div data-testid={testid}>
            <div className="flex items-baseline justify-between gap-2 mb-1">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    {label}
                </div>
                <div className="flex items-baseline gap-2">
                    {rightHint && (
                        <span className="text-[10.5px] text-muted-foreground">{rightHint}</span>
                    )}
                    <span className={`font-display text-[15px] font-bold tabular-nums ${valueCls}`}>
                        {fmtNum(value)}
                    </span>
                </div>
            </div>
            <div className="relative h-7 rounded-md bg-secondary/60 overflow-hidden border border-border">
                <div
                    className={`absolute inset-y-0 left-0 ${toneCls} transition-[width] duration-500 ease-out`}
                    style={{ width: `${widthPct}%` }}
                />
            </div>
        </div>
    );
}


// iter98.99eq — Side-by-side breakdown list (Top concerns / Top cities).
// Horizontal bars scaled to the leader's count so the dominant value
// pops without making minor entries invisible. Loading + empty states
// keep the section quiet in cold installs.
function EnrichBreakdownList({ title, icon: Icon, rows, loading, emptyText, testid }) {
    const max = Math.max(1, ...rows.map((r) => r.count || 0));
    return (
        <div className="p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={14} weight="duotone" className="text-amber-700" />
                <h3 className="text-[12.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{title}</h3>
            </div>
            {loading ? (
                <div className="text-[12px] text-muted-foreground italic">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-[12px] text-muted-foreground italic leading-relaxed">{emptyText}</div>
            ) : (
                <ul className="space-y-2">
                    {rows.map((r) => {
                        const widthPct = Math.round((r.count / max) * 100);
                        return (
                            <li key={r.label} className="flex items-center gap-3">
                                <span className="text-[12.5px] font-medium text-foreground w-32 shrink-0 truncate" title={r.label}>{r.label}</span>
                                <div className="relative flex-1 h-5 rounded-md bg-secondary/60 overflow-hidden border border-border">
                                    <div
                                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-200 to-amber-300 transition-[width] duration-500 ease-out"
                                        style={{ width: `${widthPct}%` }}
                                    />
                                </div>
                                <span className="text-[12.5px] font-bold tabular-nums w-8 text-right shrink-0">{fmtNum(r.count)}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}


// =============================================================================
// iter100.236 — Executive KPI Row + Delta Pill + inline Sparkline components.
// =============================================================================

/**
 * DeltaPill — colored ▲/▼/─ badge showing period-over-period change %.
 * Grey/muted when either value is 0 (change is meaningless without a baseline).
 * Green for improvement, rose for decline. The `invert` flag flips the tone
 * mapping — use it when "down is good" (e.g., dropoff rates).
 */
function DeltaPill({ current, previous, invert = false, suffix = "" }) {
    // Both values missing → nothing to show.
    if (current == null || previous == null) return null;
    // Zero baseline → can't compute a % change. Show a bare "new" pill for
    // the case where current > 0 and previous == 0, otherwise show nothing.
    if (previous === 0) {
        if (current === 0) return null;
        return (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200/70">
                NEW
            </span>
        );
    }
    const delta = ((current - previous) / previous) * 100;
    const rounded = Math.round(delta * 10) / 10;
    // Near-zero movement → grey neutral.
    if (Math.abs(rounded) < 0.5) {
        return (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                ─ flat
            </span>
        );
    }
    const isUp = rounded > 0;
    // "Up is good" is the default. Flip when a metric where "down is good".
    const good = invert ? !isUp : isUp;
    const cls = good
        ? "bg-emerald-100 text-emerald-800 border-emerald-200/70"
        : "bg-rose-100 text-rose-700 border-rose-200/70";
    const arrow = isUp ? "▲" : "▼";
    return (
        <span
            className={`inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full border ${cls}`}
        >
            {arrow} {Math.abs(rounded)}%{suffix}
        </span>
    );
}


/**
 * Sparkline — inline SVG polyline of daily values. Fills gaps with 0 so a
 * missing day reads as "nothing happened that day". Renders empty (returns
 * null) when there's no data — the KPI tile handles the fallback.
 * iter100.237 — Removed `overflow-visible` so the polyline is clipped
 * cleanly to its viewbox. Prevents rendering artifacts leaking sideways
 * into adjacent grid cells when a card is narrow.
 */
function Sparkline({ points, width = 60, height = 20, stroke = "currentColor" }) {
    if (!Array.isArray(points) || points.length === 0) return null;
    const values = points.map((p) => Number(p.value || 0));
    const max = Math.max(1, ...values);
    // Ensure at least 2 points so the polyline renders as a line.
    if (values.length === 1) values.push(values[0]);
    const stepX = width / Math.max(1, values.length - 1);
    const coords = values.map((v, i) => {
        const x = i * stepX;
        // Reserve 2px top+bottom padding so the line doesn't kiss the edges.
        const y = height - 2 - ((v / max) * (height - 4));
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    // Area fill under the line for a subtle "you feel the trend" effect.
    const areaPoints = `0,${height} ${coords} ${width},${height}`;
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
            <polyline
                points={areaPoints}
                fill={stroke}
                fillOpacity="0.14"
                stroke="none"
            />
            <polyline
                points={coords}
                fill="none"
                stroke={stroke}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}


/**
 * KpiTile — one tile in the Executive Row. Big number, delta pill next to it,
 * a tiny sparkline anchored to the bottom-right (paired with the caption
 * so nothing competes with the label or value for horizontal space), and a
 * click handler that scrolls the target funnel section into view.
 * `overflow-hidden` guarantees no child leaks into adjacent grid cells even
 * at very narrow card widths.
 * iter100.237 — Layout rebuilt: label on row 1 (full width, no truncation),
 * value + delta pill on row 2 (with flex-wrap fallback for very narrow
 * cards), caption + sparkline on row 3. Fixes the "delta pill drops below
 * number", "sparkline overflows into next card", and "label truncated
 * because sparkline stole the top row" bugs from the initial ship.
 */
function KpiTile({
    label, value, previous, sparkline, sparklineColor, loading,
    invertDelta = false, testid, onClick, valueSuffix = "",
}) {
    const hasSparkline = Array.isArray(sparkline) && sparkline.length > 0;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left p-4 rounded-2xl border border-border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all duration-150 group overflow-hidden ${onClick ? "cursor-pointer" : "cursor-default"}`}
            data-testid={testid}
        >
            {/* Row 1 — label gets the full width, no truncation. */}
            <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                {label}
            </div>
            {/* Row 2 — value + delta pill on their own line. */}
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <div className="font-display text-[24px] font-bold tabular-nums leading-none">
                    {loading ? "—" : (value ?? "—")}{valueSuffix}
                </div>
                {!loading && (
                    <DeltaPill current={value} previous={previous} invert={invertDelta} />
                )}
            </div>
            {/* Row 3 — caption (left) + sparkline (right, bottom-anchored). */}
            <div className="mt-2 flex items-end justify-between gap-2 min-h-[20px]">
                <div className="text-[10.5px] text-muted-foreground/70">
                    vs previous period
                </div>
                {hasSparkline && (
                    <div className={`${sparklineColor || "text-muted-foreground/70"} flex-shrink-0`}>
                        <Sparkline points={sparkline} />
                    </div>
                )}
            </div>
        </button>
    );
}
